import { Injectable } from '@angular/core';
import { GfTranslateService } from '../../../services/core.translate.service';
import { ProjectModel } from '../../../services/project.model.service';

import * as _ from 'lodash-es';

@Injectable()
export class OrgHelper {

    constructor(
        private gfTranslateService: GfTranslateService,
        private projectModel: ProjectModel) {}

    /** BREADCRUMBS **/

    /**
     * Flatten the set of project.parent -> .parent -> .parent refs
     * into a single array.
     *
     * Returns an array, where element 0 is the root parent,
     * and the last element is the specified project.
     *
     * If the project has no parent, a single element array will be returned.
     */
    getProjectHierarchy(project) {
        var ret = [];
        var item = project;
        while (item) {
            ret.push(item);
            item = item.parent;
        }
        return ret.reverse();
    }

    /**
     * Transform the array of projects into breadcrumb data.
     *
     * NOTE: The data produced here is custom to the markup
     * used in Site Admin Orgs, not for gf-breadcrumb or
     * any other standard renderer.
     *
     * NOTE: The "Orgs" top-level item is always added, even
     * if the project is null.
     */
    getOrgBreadcrumbs(project) {
        var base = [
            {
                label: this.gfTranslateService.translate("Orgs"),
                selectedProject: null
            }
        ];

        var crumbs = this.getProjectHierarchy(project)
            .map(function (p) {
                return {
                    label: p.name,
                    selectedProject: p.unixName
                };
            });

        return base.concat(crumbs);
    };
    /** BREADCRUMBS **/


    /** PROJECT TREE **/

    /**
     * For each object in the array of projects,
     * set the parent property using other items
     * in the array.
     *
     * Returns an array with the original objects,
     * after adding a parent proeprty to each.
     *
     * NOTE: Parent can be null for root-level
     * projects, and if the parentProjectId is not
     * found in the source array.
     */
    setProjectParents(projects) {
        return _.map(projects, function (project) {
            project.parent = _.find(projects, { id: project.parentProjectId });
            return project;
        });
    };

    /**
     * For each object in the array of projects,
     * set the children property using other items
     * in the array.
     *
     * Returns an array with the original objects,
     * after adding a children property to each.
     *
     * NOTE: Children can be empty array.
     */
    setProjectChildren(projects) {
        return _.map(projects, function (project) {
            project.children = _.filter(projects, { parentProjectId: project.id });
            return project;
        });
    };

    /**
     * Returns the set of project objects in the
     * source array that have parentProjectId = 0.
     *
     * NOTE: Elements are sorted by project name.
     */
    getRootElements(projects) {
        return _.sortBy(
            _.filter(projects, { parentProjectId: 0 }),
            'name'
        );
    };

    /**
     * Sets parent and children properties for all items in the array of projects.
     *
     * Returns thea new array, containing the original objects, after adding
     * the parent and children properties.
     */
    buildProjectTree(projects) {
        return this.setProjectParents(
            this.setProjectChildren(projects)
        );
    };

    /**
     * Add items from newProjects to currentList that are not already there.
     *
     * Returns a new array of objects, with parent and children properties set.
     */
    addProjectsToTree(currentList, newProjects) {
        return this.buildProjectTree(
            _.uniqBy(
                _.concat(currentList, newProjects),
                'id'
            )
        );
    };
    /** PROJECT TREE **/


    /** FILTER FUNCTIONS **/
    getDefaultSearchParams() {
        return { parentProjectId: 0, rel: 'childCount', page_size: 100, isOrg: 1 };
    };

    getSearchParams(params) {
        return _.merge(this.getDefaultSearchParams(), params);
    };

    createOrgFilters() {
        return {
            main_fields: [
                { alias: "text", fieldName: this.gfTranslateService.translate("Text Search"), value: '', fieldType: 4, isExtraField: false },
                { alias: "status", fieldName: this.gfTranslateService.translate("Status"), value: { id: '', label: "Any" }, fieldType: 1, isExtraField: false, fieldElements: [{ id: '', label: this.gfTranslateService.translate("Any") }, { id: '0', label: this.gfTranslateService.translate("Pending") }, { id: '1', label: this.gfTranslateService.translate("Active") }, { id: '3', label: this.gfTranslateService.translate("Holding") }] },
                { alias: "isPublic", fieldName: this.gfTranslateService.translate("Visibility"), value: { id: '', label: "Any" }, fieldType: 1, isExtraField: false, fieldElements: [{ id: '', label: this.gfTranslateService.translate("Any") }, { id: 'Y', label: this.gfTranslateService.translate("Public") }, { id: 'N', label: this.gfTranslateService.translate("Private") }] }
            ]
        };
    };

    // TODO - these next three functions can probably be
    // refactored into their own class, and re-used
    // anywhere the filter widget is used.
    getFilterValues(filterList) {

        // Flatten all sections into a single array
        var flattenedFilters = _.reduce(
            filterList,
            function (carry, section) {
                return _.concat(carry, section);
            },
            []
        );

        // Get all values - the querystring/ui-router will
        // ignore empty values.

        // If we don't include them here, they will always
        // inherit the value in the query string, and user will
        // never be able to "unset" a filter.
        return _.reduce(
            flattenedFilters,
            function (carry, filter, key) {
                if ((filter?.value ?? null) !== null) {
                    var value = _.get(
                        filter, 'value.id',
                        _.get(filter, 'value', '')
                    );

                    carry[filter.alias] = value;
                    return carry;
                }
            },
            {}
        );
    };

    setFilterValues(filterList, values) {
        _.each(filterList, function (filters) {
            _.each(filters, function (filter) {
                filter.value = _.get(
                    values, filter.alias, ''
                );
                if (filter.fieldElements) {
                    filter.value = _.find(filter.fieldElements, { id: filter.value });
                    if (!filter.value) {
                        filter.value = _.get(filter, 'fieldElements[0]', '');
                    }
                }
            });
        });

        return filterList;
    }
    /** FILTER FUNCTIONS **/


    /** DISPLAY FUNCTIONS **/
    getProjectStatusClass(status) {
        switch (status) {
            case 0:
                return 'alert-danger';
            case 1:
                return 'alert-success';
            default:  // include 3 = on hold/deleted
                return 'alert-warning';
        }
    };

    getProjectStatus(status) {
        switch (status) {
            case 0:
                return this.projectModel.status.pending.label;
            case 1:
                return this.projectModel.status.active.label;
            case 3:
                return this.projectModel.status.onhold.label;
            default:
                return "(unknown)";
        }
    };
    /** DISPLAY FUNCTIONS **/
}